import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Per-user preferences.
 *
 * Stored as a jsonb column on user_profiles and reached only through two
 * SECURITY DEFINER functions. The obvious shortcut, an RLS policy letting a
 * user update their own row, would have been a privilege escalation: RLS
 * cannot scope an UPDATE to one column, so any signed-in user could have set
 * their own role to admin. The RPCs touch nothing but the preferences column
 * of the caller's own row, so user_profiles keeps its admin-only update policy
 * exactly as it was.
 *
 * localStorage is banned in this codebase, and it would be wrong here anyway.
 * A column Andrea hid at the office should still be hidden at home.
 */

export interface BoardPreferences {
    /**
     * Column ids the user has hidden on the schedule board.
     *
     * Hidden rather than visible on purpose. Store the visible list and any
     * column added later is invisible to everyone who ever saved a preference,
     * and they never find out it exists. Storing what was hidden means a new
     * column shows up for everyone by default, which is the safe direction.
     */
    hiddenColumns?: string[];
}

export interface UserPreferences {
    /** Schedule board. */
    board?: BoardPreferences;
    /**
     * Service board. Its own key rather than a shared one, because the two
     * boards have different columns and a shared list would hide "notes" on
     * both when someone only meant one.
     */
    service?: BoardPreferences;
}

export const userPreferencesKey = ['userPreferences'] as const;

export function useUserPreferences() {
    return useQuery({
        queryKey: userPreferencesKey,
        queryFn: async (): Promise<UserPreferences> => {
            const { data, error } = await supabase.rpc('get_user_preferences');
            if (error) throw new Error(error.message);
            return (data ?? {}) as UserPreferences;
        },
        // Preferences change when the person changes them, not on their own.
        staleTime: 1000 * 60 * 5,
    });
}

export function useSetUserPreferences() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (next: UserPreferences): Promise<UserPreferences> => {
            const { data, error } = await supabase.rpc('set_user_preferences', {
                p_preferences: next,
            });
            if (error) throw new Error(error.message);
            return (data ?? {}) as UserPreferences;
        },
        // Ticking a checkbox has to move the column immediately. Waiting on a
        // round trip to hide a column reads as a broken checkbox, so the cache
        // moves first and rolls back if the write fails.
        onMutate: async (next) => {
            await qc.cancelQueries({ queryKey: userPreferencesKey });
            const previous = qc.getQueryData<UserPreferences>(userPreferencesKey);
            qc.setQueryData(userPreferencesKey, next);
            return { previous };
        },
        onError: (_err, _next, ctx) => {
            if (ctx?.previous !== undefined) {
                qc.setQueryData(userPreferencesKey, ctx.previous);
            }
        },
        onSuccess: (data) => {
            qc.setQueryData(userPreferencesKey, data);
        },
    });
}
